import React from 'react';
import { motion } from 'framer-motion';
import Dashboard from './dashboard/Dashboard';
import SectionTransitionEffect from './ui/SectionTransitionEffect';

const Hero: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1.0] }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1.0], delay: 0.7 }
    }
  };

  const logoVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    },
    hover: {
      scale: 1.05,
      transition: { duration: 0.2 }
    }
  };

  return (
    <section id="home" className="pt-28 md:pt-32 pb-20 md:pb-32 overflow-hidden relative">
      {/* Background gradient */}
      <motion.div 
        className="absolute rounded-full opacity-70 blur-3xl -z-10 w-[800px] h-[800px] top-[-400px] left-[10%]"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.7, scale: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(204, 255, 0, 0.3), rgba(204, 255, 0, 0) 70%)'
        }}
      />
      
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center md:space-x-8">
          {/* Left Column - Value Proposition */}
          <motion.div 
            className="md:w-1/2 mb-12 md:mb-0"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div className="inline-block px-3 py-1 mb-6 rounded-full bg-accent/10 border border-accent/20">
              <motion.span 
                className="text-accent font-semibold text-sm"
                variants={itemVariants}
              >
                New: DetailerOS + GRIT SUITE Bundle
              </motion.span>
            </motion.div>
            
            <motion.h1 
              className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-6"
              variants={itemVariants}
            >
              Transform Your <span className="text-accent">Detailing Business</span> With DetailerOS
            </motion.h1>
            
            <motion.p 
              className="text-xl text-white/80 mb-10 max-w-2xl leading-relaxed"
              variants={itemVariants}
            >
              Expert coaching, actionable advice, and powerful software combined in one solution to help detailing professionals scale their businesses.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 items-center"
              variants={buttonVariants}
            >
              <motion.a 
                href="#signup" 
                className="btn btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
              >
                Start Your Free Trial
              </motion.a>
              <motion.a 
                href="#demo" 
                className="btn btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
              >
                Schedule a Demo
              </motion.a>
            </motion.div>
            
            <motion.div 
              className="mt-10 flex items-center"
              variants={itemVariants}
            >
              <div className="flex items-center">
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-4 h-4 text-accent" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-sm text-white/70 ml-3">
                  <span className="font-semibold text-white">4.9/5</span> from other shop owners
                </p>
              </div>
            </motion.div>
          </motion.div>
          
          {/* Right Column - Interactive Dashboard Preview */}
          <div className="md:w-1/2">
            <SectionTransitionEffect delay={0.5}>
              <motion.div 
                className="relative z-10"
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Dashboard />
              </motion.div>
            </SectionTransitionEffect>
          </div>
        </div>

        {/* Logos and Combination Message */}
        <motion.div 
          className="mt-16 md:mt-24 max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
        >
          <div className="glassmorphic rounded-xl p-6 md:p-10 border border-accent/20 shadow-glow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
              <span className="text-accent">Triple</span> the Power, <span className="text-accent">Triple</span> the Results
            </h2>
            
            <div className="flex flex-col md:flex-row items-center justify-around gap-8 mb-8">
              <motion.div 
                className="flex flex-col items-center"
                variants={logoVariants}
                initial="hidden"
                animate="visible"
                whileHover="hover"
              >
                <img 
                  src="https://dgstorage.b-cdn.net/detaileros-logo.png" 
                  alt="DetailerOS Logo" 
                  className="h-16 md:h-20 object-contain mb-2" 
                />
                <p className="text-lg font-medium">Business Coaching</p>
              </motion.div>
              
              <div className="text-3xl font-bold text-accent">+</div>
              
              <motion.div 
                className="flex flex-col items-center"
                variants={logoVariants}
                initial="hidden"
                animate="visible"
                whileHover="hover"
                transition={{ delay: 0.2 }}
              >
                <img 
                  src="https://dgstorage.b-cdn.net/GRIT-300x137%20(1).png" 
                  alt="GRIT SUITE Logo" 
                  className="h-16 md:h-20 object-contain mb-2" 
                />
                <p className="text-lg font-medium">Technology Platform</p>
              </motion.div>
              
              <div className="text-3xl font-bold text-accent">+</div>
              
              <motion.div 
                className="flex flex-col items-center"
                variants={logoVariants}
                initial="hidden"
                animate="visible"
                whileHover="hover"
                transition={{ delay: 0.4 }}
              >
                <img 
                  src="https://bunny-wp-pullzone-1a77f66mfv.b-cdn.net/wp-content/uploads/2024/07/New-Logo-300x163.png" 
                  alt="Detailing Growth Logo" 
                  className="h-16 md:h-20 object-contain mb-2" 
                />
                <p className="text-lg font-medium">Marketing Agency</p>
              </motion.div>
            </div>
            
            <div className="text-center mb-6">
              <div className="text-4xl font-bold flex items-center justify-center">
                <span className="mr-4">=</span>
                <span className="text-accent">DOMINATION</span>
              </div>
            </div>
            
            <p className="text-xl text-center max-w-3xl mx-auto">
              Get all the tools you need to succeed from one place. Build, grow, and scale your detailing business with our complete solution.
            </p>
            
            <div className="flex justify-center mt-8">
              <motion.button 
                className="btn btn-primary text-lg px-8 py-4"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Started Today
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;